/**
 * What a killed monster leaves (spec 02 §7.2). On the C64 it is the monster's own sprite: it flies off
 * (state $0B, D_1A6F + L267B), falls 2 px per I-tick to a platform (state $0C, D_273D), turns into its
 * item (state $11, score_display $2921 - one sprite per I-tick) and lasts 200 I-ticks (state $12,
 * D_29BB), collected by a player within 16 px (D_29C0). Here the item exists from the kill, `falling`
 * until it has turned, and carries its flight in `death` (a structural extension of the contract's
 * `Item`, plain data; contract gap - Item has no stream-owned sub-object).
 */

import { rngOf } from '../random';
import { addScore } from '../score';
import { GONE } from './codes';
import { DIED_IN_FIRE, DIED_OTHERWISE, DIED_UNDER_HEART, deathPrize } from './food';
import { mapSolid, setCode } from './slots';
import {
  FALL_PX, FLIGHT_PERIOD, FLIGHT_TIMER, FLIGHT_X_MAX, FLIGHT_X_MIN, ITEM_LIFE, ITEM_RANGE, LAND_MIN_Y,
  WRAP_Y_BOTTOM, WRAP_Y_TOP,
} from './tuning';
import type { Item, Monster, World } from '../types';

/** The dead monster's sprite on its way to becoming the item (the C64's $87A0/$87C8/$8868/$87F0/$8818). */
export interface DeathFlight {
  /** The monster it was (the view draws it spinning, light blue, until `stage` is 'item'). */
  monsterId: number;
  /** Its sprite slot: the C64 runs the sprites from the last down. */
  slot: number;
  /** 'fly' the arc; 'fall' straight down; 'land' landed, turning next tick; 'item' the item. */
  stage: 'fly' | 'fall' | 'land' | 'item';
  speedX: number;
  left: boolean;
  speedY: number;
  rising: boolean;
  /** The fall's top speed, rolled at the top of the arc. */
  peak: number;
  timer: number;
}

export type DyingItem = Item & { death: DeathFlight };

/** The flight of a death item, or null for any other item. */
export function deathFlight(item: Item): DeathFlight | null {
  const d = (item as Partial<DyingItem>).death;
  return d ?? null;
}

/** How a monster died, as the `monsterDied` event says it. */
export type DeathHow = 'pop' | 'water' | 'fire' | 'lightning' | 'other';

/**
 * A monster dies: `prize` is the C64's $8890 index (1..6 its place in a chain, 7 water, ...). Every
 * death but the water's goes through D_1A6F, so it flies off first (speeds rand(8) + 1, a random
 * side); from the water it falls from where the stream left it (spawn_position_init: straight to $0C).
 */
export function killMonster(world: World, mon: Monster, prize: number, how: DeathHow): void {
  mon.state = 'dead';
  mon.bubbleId = -1;
  world.events.push({ type: 'monsterDied', monsterId: mon.id, how });
  const slot = world.monsters.indexOf(mon);
  const death: DeathFlight = {
    monsterId: mon.id, slot, stage: 'fall', speedX: 0, left: false, speedY: 0, rising: false, peak: 0,
    timer: FLIGHT_TIMER,
  };
  if (how !== 'water') {
    const rng = rngOf(world);
    death.stage = 'fly';
    death.speedX = (rng.int(256) & 7) + 1;
    death.speedY = (rng.int(256) & 7) + 1;
    death.rising = true;
    death.left = (rng.int(256) & 1) === 1;
  }
  const p = deathPrize(prize);
  const item: DyingItem = {
    id: world.nextId++, kind: 'death', item: p.food, x: mon.x & 0xff, y: mon.y & 0xff, value: p.units,
    life: ITEM_LIFE, falling: true, death,
  };
  world.items.push(item);
}

/**
 * A monster killed outside a bubble's burst (M2): fire, a bolt, a strike, an explosion, the flood, the
 * heart. Every one is D_1A6F - the monster flies off and lands as its item, $8892 = 9 (a purple diamond)
 * unless the caller sets its own: fire decrements it to 8 (check_collision, $1399: a green diamond), the
 * heart's touch stores $0A ($2800: grapes). No chain counts it, nobody is paid at the kill: the item goes
 * to whoever takes it. A monster held in a bubble is let out first, the bubble vanishing without a burst
 * (the yellow cross's L_19E7: $38); one carried by the water just leaves it.
 */
export function killBy(world: World, monsterId: number, how: 'fire' | 'lightning' | 'heart' | 'other'): void {
  const mon = world.monsters.find((m) => m.id === monsterId);
  if (!mon || mon.state === 'dead') return;
  if (mon.state === 'trapped' && mon.bubbleId >= 0) {
    const b = world.bubbles.find((o) => o.id === mon.bubbleId);
    if (b && b.content.kind === 'monster') {
      b.content = { kind: 'empty' };
      b.motion.flash = false;
      b.motion.flashOff = false;
      setCode(b, GONE);
    }
  }
  const prize = how === 'fire' ? DIED_IN_FIRE : how === 'heart' ? DIED_UNDER_HEART : DIED_OTHERWISE;
  killMonster(world, mon, prize, how === 'heart' ? 'other' : how);
}

/** One I-tick of the death items, the last sprite slot first. */
export function updateDeathItems(world: World): void {
  const dying = world.items.filter((i): i is DyingItem => deathFlight(i) !== null)
    .sort((a, b) => b.death.slot - a.death.slot);
  let turned = false;
  for (const it of dying) {
    const d = it.death;
    if (d.stage === 'fly') fly(world, it);
    else if (d.stage === 'fall') fall(world, it);
    else if (d.stage === 'land') {
      // score_display: the self-modified flag at $2922 lets one sprite turn per I-tick.
      if (turned) continue;
      turned = true;
      d.stage = 'item';
      it.falling = false;
      world.events.push({ type: 'item', itemId: it.id, spawned: true });
    } else if (--it.life <= 0) {
      remove(world, it);
      world.events.push({ type: 'item', itemId: it.id, spawned: false });
    } else {
      collect(world, it);
    }
  }
}

/**
 * L267B / L_26BF: every I-tick X moves by the side speed (bouncing off $24 and $F4) and Y by the up or
 * down speed; after 7 ticks, then every 4, the rising speed drops by 1; at 0 the top speed of the
 * fall is rolled (1..8), and the falling speed then grows by 1 each time until it passes it: the
 * sprite snaps to even X, odd Y and falls straight (no move that tick).
 */
function fly(world: World, it: DyingItem): void {
  const d = it.death;
  if (--d.timer < 0) {
    d.timer = FLIGHT_PERIOD;
    if (d.rising) {
      if (--d.speedY === 0) {
        d.rising = false;
        d.peak = (rngOf(world).int(256) & 7) + 1;
      }
    } else if (++d.speedY > d.peak) {
      it.x &= 0xfe;
      it.y |= 1;
      d.stage = 'fall';
      return;
    }
  }
  if (d.left) {
    it.x -= d.speedX;
    if (it.x < FLIGHT_X_MIN) {
      it.x = FLIGHT_X_MIN;
      d.left = false;
    }
  } else {
    it.x += d.speedX;
    if (it.x >= FLIGHT_X_MAX) {
      it.x = FLIGHT_X_MAX;
      d.left = true;
    }
  }
  if (d.rising) {
    it.y -= d.speedY;
    if (it.y < WRAP_Y_TOP) it.y = WRAP_Y_BOTTOM;
  } else {
    it.y += d.speedY;
    if (it.y >= WRAP_Y_BOTTOM) it.y = WRAP_Y_TOP;
  }
}

/**
 * D_273D: down 2 px (Y $F5 wraps to $15). On each tile line from Y $1F: if a cell beside the lower
 * body is solid it falls on through; else a solid cell under the feet lands it. The cells are those of
 * the columns under the body (a third when X is off the grid), probed before the move (D_E9B8).
 */
function fall(world: World, it: DyingItem): void {
  const top = (((it.y - 0x15) & 0xff) >> 3) - 3;
  const col = ((it.x - 0x14) & 0xff) >> 3;
  const cols = ((it.x - 0x14) & 7) !== 0 ? [col, col + 1, col + 2] : [col, col + 1];
  it.y = (it.y + FALL_PX) & 0xff;
  if (it.y === WRAP_Y_BOTTOM) it.y = WRAP_Y_TOP;
  if (it.y < LAND_MIN_Y || ((it.y - 0x2d) & 7) !== 0) return;
  if (cols.some((c) => mapSolid(world, c, top + 2))) return;
  if (cols.some((c) => mapSolid(world, c, top + 3))) it.death.stage = 'land';
}

/** D_29C0: player 2, then 1; a dying player still collects (the C64 skips only state $0F). */
function collect(world: World, it: DyingItem): void {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state !== 'play' && p.state !== 'dying') continue;
    if (Math.abs(p.x - it.x) >= ITEM_RANGE || Math.abs(p.y - it.y) >= ITEM_RANGE) continue;
    addScore(world, id, it.value);
    world.events.push({ type: 'collect', player: id, itemId: it.id, value: it.value });
    remove(world, it);
    return;
  }
}

function remove(world: World, it: Item): void {
  const i = world.items.indexOf(it);
  if (i >= 0) world.items.splice(i, 1);
}
